import React, { useState, useEffect } from 'react';
import { supplierAPI } from '../services/supplierApi';
import { orderAPI } from '../services/orderApi';
import { Supplier, SupplierRequest, Order } from '../types';
import { useCompany } from '../context/CompanyContext';
import { useI18n } from '../hooks/useI18n';
import Layout from '../components/Layout';
import { Plus, Edit, Trash2, Eye, Search, AlertCircle, CheckCircle, X } from 'lucide-react';
import { clientAPI } from "../services/api";

const SuppliersPage: React.FC = () => {
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState<Supplier | null>(null);
  const [editingSupplier, setEditingSupplier] = useState<Supplier | null>(null);

  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();

  useEffect(() => {
    if (selectedCompany.selectedCompanyId) {
      loadSuppliers();
    } else {
      setSuppliers([]);
      setLoading(false);
    }
  }, [selectedCompany.selectedCompanyId]);

  const loadSuppliers = async () => {
    try {
      setLoading(true);
      const data = await supplierAPI.getSuppliersByCompany(selectedCompany.selectedCompanyId!);
      setSuppliers(data);
      setError(null);
    } catch (err) {
      setError(t('suppliers.failedToLoad'));
      console.error('Error loading suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOrUpdate = async (supplierData: SupplierRequest) => {
    try {
      if (editingSupplier) {
        const updated = await supplierAPI.updateSupplier(editingSupplier.id, supplierData);
        setSuppliers(suppliers.map(s => s.id === editingSupplier.id ? updated : s));
        setSuccessMessage(t('suppliers.supplierUpdated'));
      } else {
        const created = await supplierAPI.createSupplier(supplierData);
        setSuppliers([...suppliers, created]);
        setSuccessMessage(t('suppliers.supplierCreated'));
      }
      setShowModal(false);
      setEditingSupplier(null);
    } catch (err) {
      setError(t('suppliers.failedToUpdate'));
      console.error('Error saving supplier:', err);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('suppliers.confirmDelete'))) {
      try {
        await supplierAPI.deleteSupplier(id);
        setSuppliers(suppliers.filter(s => s.id !== id));
        setSuccessMessage(t('suppliers.supplierDeleted'));
      } catch (err) {
        setError(t('suppliers.failedToDelete'));
        console.error('Error deleting supplier:', err);
      }
    }
  };

  const filteredSuppliers = suppliers.filter(s =>
    s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (s.email && s.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
    (s.phone && s.phone.includes(searchTerm)) ||
    (s.ice && s.ice.includes(searchTerm))
  );

  if (!selectedCompany.selectedCompanyId) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-64">
          <div className="text-center">
            <AlertCircle className="w-16 h-16 text-yellow-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('clients.noCompanySelected')}</h2>
            <p className="text-gray-600 mb-4">{t('clients.noCompanyMessage')}</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className={`flex justify-between items-center mb-6 ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('suppliers.title')}</h1>
          <p className="text-gray-600 mt-1">
            {t('suppliers.subtitle', { companyName: selectedCompany.selectedCompany?.name })}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => {
              setEditingSupplier(null);
              setShowModal(true);
            }}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center gap-2"
          >
            <Plus size={20} />
            {t('suppliers.createNew')}
          </button>
        </div>
      </div>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <span>{error}</span>
          <button onClick={() => setError(null)}><X size={18} /></button>
        </div>
      )}

      {successMessage && (
        <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded mb-4 flex justify-between items-center">
          <div className="flex items-center gap-2">
            <CheckCircle size={18} />
            <span>{successMessage}</span>
          </div>
          <button onClick={() => setSuccessMessage(null)}><X size={18} /></button>
        </div>
      )}

      <div className="mb-6">
        <div className="relative">
          <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-gray-400 z-10`} size={20} />
          <input
            type="text"
            placeholder={t('suppliers.searchPlaceholder')}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`w-full ${isRTL ? 'pr-10 pl-4' : 'pl-10 pr-4'} py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
          />
        </div>
      </div>

      <div className="bg-white shadow-md rounded-lg overflow-hidden overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('suppliers.name')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('suppliers.email')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('suppliers.phone')}
              </th>
              <th className={`hidden md:table-cell px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('suppliers.city')}
              </th>
              <th className={`px-6 py-3 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                {t('suppliers.actions')}
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredSuppliers.map((supplier) => (
              <tr key={supplier.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap">
                  <div className="text-sm font-medium text-gray-900">{supplier.name}</div>
                  {supplier.ice && (
                    <div className="text-sm text-gray-500">{t('suppliers.ice')}: {supplier.ice}</div>
                  )}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {supplier.email || '-'}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {supplier.phone || '-'}
                </td>
                <td className="hidden md:table-cell px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                  {supplier.city || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => {
                        setSelectedSupplier(supplier);
                        setShowViewModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => {
                        setEditingSupplier(supplier);
                        setShowModal(true);
                      }}
                      className="text-green-600 hover:text-green-900"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleDelete(supplier.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredSuppliers.length === 0 && !loading && (
          <div className="text-center py-8 text-gray-500">
            {t('suppliers.noSuppliersFound')}
          </div>
        )}
      </div>

      {showModal && (
        <SupplierModal
          supplier={editingSupplier}
          onClose={() => setShowModal(false)}
          onSubmit={handleCreateOrUpdate}
        />
      )}

      {showViewModal && selectedSupplier && (
        <SupplierViewModal
          supplier={selectedSupplier}
          onClose={() => setShowViewModal(false)}
        />
      )}
    </Layout>
  );
};

interface SupplierModalProps {
  supplier: Supplier | null;
  onClose: () => void;
  onSubmit: (data: SupplierRequest) => void;
}

const SupplierModal: React.FC<SupplierModalProps> = ({ supplier, onClose, onSubmit }) => {
  const { selectedCompany } = useCompany();
  const { t, isRTL } = useI18n();
  const [formData, setFormData] = useState<SupplierRequest>({} as SupplierRequest);
  const initSupplierData = () => {

    setFormData({
      companyId: selectedCompany.selectedCompanyId || '',
      supplierNumber: supplier?.supplierNumber,
      name: supplier?.name || '',
      email: supplier?.email || '',
      phone: supplier?.phone || '',
      ice: supplier?.ice || '',
      street: supplier?.street || '',
      city: supplier?.city || '',
      postalCode: supplier?.postalCode || '',
      country: supplier?.country || '',
      website: supplier?.website || '',
    });
    supplierAPI.getNextSupplierNumber(selectedCompany.selectedCompanyId || '').then(
      nextNumber => {
        setFormData(prev => ({
          ...prev,
          supplierNumber: supplier?.supplierNumber || nextNumber
        }))
      }
    );
  }
  // Fetch next supplier number when component mounts or company changes
  useEffect(() => {
    initSupplierData()
  }, [selectedCompany.selectedCompanyId]);


  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-2xl font-bold mb-4">
          {supplier ? t('suppliers.editSupplier') : t('suppliers.createNew')}
        </h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Supplier Number
              </label>
              <input
                type="number"
                value={formData.supplierNumber || ''}
                onChange={(e) => setFormData({ ...formData, supplierNumber: parseInt(e.target.value) || 0 })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
                placeholder="Auto-generated"
              />
            </div>
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.name')} *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.email')}
              </label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.phone')}
              </label>
              <input
                type="tel"
                value={formData.phone || ''}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.ice')}
              </label>
              <input
                type="text"
                value={formData.ice || ''}
                onChange={(e) => setFormData({ ...formData, ice: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.website')}
              </label>
              <input
                type="url"
                value={formData.website || ''}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.street')}
              </label>
              <input
                type="text"
                value={formData.street || ''}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.city')}
              </label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.postalCode')}
              </label>
              <input
                type="text"
                value={formData.postalCode || ''}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {t('suppliers.country')}
              </label>
              <input
                type="text"
                value={formData.country || ''}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className={`flex justify-end gap-3 pt-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
            >
              {t('suppliers.cancel')}
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            >
              {supplier ? t('suppliers.update') : t('suppliers.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

interface SupplierViewModalProps {
  supplier: Supplier;
  onClose: () => void;
}

const SupplierViewModal: React.FC<SupplierViewModalProps> = ({ supplier, onClose }) => {
  const { t, isRTL } = useI18n();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        const data = await orderAPI.getOrdersBySupplier(supplier.id);
        setOrders(data);
      } catch (err) {
        console.error('Error fetching supplier orders:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOrders();
  }, [supplier.id]);

  const totalTransactions = orders.reduce((sum, order) => sum + order.totalAmount, 0);
  const totalPaid = orders.reduce((sum, order) => {
    const paid = order.paymentHistory.reduce((pSum, p) => pSum + p.amount, 0);
    return sum + paid;
  }, 0);
  const remainingBalance = totalTransactions - totalPaid;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto ${isRTL ? 'rtl' : 'ltr'}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <h2 className="text-2xl font-bold mb-4">{t('suppliers.supplierDetails')}</h2>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Supplier Number</label>
            <p className="text-sm text-gray-900">{supplier.supplierNumber || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.name')}</label>
            <p className="text-sm text-gray-900">{supplier.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.ice')}</label>
            <p className="text-sm text-gray-900">{supplier.ice || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.email')}</label>
            <p className="text-sm text-gray-900">{supplier.email || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.phone')}</label>
            <p className="text-sm text-gray-900">{supplier.phone || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.website')}</label>
            <p className="text-sm text-gray-900">{supplier.website || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.street')}</label>
            <p className="text-sm text-gray-900">{supplier.street || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.city')}</label>
            <p className="text-sm text-gray-900">{supplier.city || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.postalCode')}</label>
            <p className="text-sm text-gray-900">{supplier.postalCode || '-'}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">{t('suppliers.country')}</label>
            <p className="text-sm text-gray-900">{supplier.country || '-'}</p>
          </div>
        </div>

        <div className="border-t pt-4 mt-6">
          <h3 className="text-lg font-bold mb-3">{t('clients.financialSummary')}</h3>
          <div className="grid grid-cols-3 gap-4 bg-gray-50 p-4 rounded-lg">
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">{t('clients.totalTransactions')}</label>
              <p className="text-lg font-bold text-gray-900">{totalTransactions.toFixed(2)}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">{t('clients.totalPaid')}</label>
              <p className="text-lg font-bold text-green-600">{totalPaid.toFixed(2)}</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-500 uppercase">{t('clients.remainingBalance')}</label>
              <p className="text-lg font-bold text-red-600">{remainingBalance.toFixed(2)}</p>
            </div>
          </div>
        </div>

        <div className="border-t pt-4 mt-6">
          <h3 className="text-lg font-bold mb-3">{t('clients.orderHistory')}</h3>
          {loading ? (
            <div className="flex justify-center py-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : orders.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className={`px-4 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase`}>{t('invoices.date')}</th>
                    <th className={`px-4 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase`}>{t('stock.orders.status')}</th>
                    <th className={`px-4 py-2 ${isRTL ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase text-right`}>{t('invoices.total')}</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id}>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900">
                        {new Date(order.creationDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${order.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                          order.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                            order.status === 'CANCELLED' ? 'bg-red-100 text-red-800' :
                              'bg-yellow-100 text-yellow-800'
                          }`}>
                          {t(`stock.orders.${order.status.toLowerCase()}`)}
                        </span>
                      </td>
                      <td className="px-4 py-2 whitespace-nowrap text-sm text-gray-900 text-right">
                        {order.totalAmount.toFixed(2)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="text-sm text-gray-500 italic">{t('clients.noOrdersFound')}</p>
          )}
        </div>
        <div className="flex justify-end pt-4">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
          >
            {t('suppliers.close')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SuppliersPage;
